import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { getAccessToken } from './auth';
import { useNpsAccess } from './useNpsAccess';
import {
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Menu,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CapContactsDrawer from './CapContactsDrawer';
import NpsMemberEmailLookup from './NpsMemberEmailLookup';
import NpsSubmittedDateRangeSlider from './NpsSubmittedDateRangeSlider';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

import {
  NPS_RESULTS_URL,
  broadcastNpsOpenCount,
  fetchNpsOpenCount,
  updateNpsResult,
  buildNpsResultsSearchParams,
} from './npsApi';
import {
  NPS_SURVEY_TYPE_OPTIONS,
  getNpsSurveyLabel,
} from './npsSurveyTypes';
import { getNpsResolutionDisplay } from './npsResolution';
import {
  DEFAULT_NPS_STATUS_FILTER,
  NPS_STATUS_OPTIONS,
  canNpsQueueActions,
  canNpsLeadershipClose,
  canNpsReopen,
  getNpsItemStatus,
  isDefaultNpsStatusFilter,
  itemMatchesNpsStatusFilter,
  NPS_ASSIGN_TEAMS,
  showNpsResolutionUi,
} from './npsWorkflow';

const NPS_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

const NPS_BUCKET_OPTIONS = [
  { value: '', label: 'All scores' },
  { value: 'detractor', label: 'Detractor (0–6)' },
  { value: 'neutral', label: 'Neutral (7–8)' },
  { value: 'promoter', label: 'Promoter (9–10)' },
];

const npsStatusChipProps = (item) => {
  const status = getNpsItemStatus(item);
  switch (status) {
    case 'assigned':
      return { label: 'Assigned', color: 'info', variant: 'outlined' };
    case 'resolved':
      return { label: 'Resolved', color: 'success', variant: 'outlined' };
    case 'closed':
      return { label: 'Closed', color: 'default', variant: 'outlined' };
    default:
      return { label: 'Open', color: 'warning', variant: 'filled' };
  }
};

const followUpKindLabel = (kind) => {
  if (!kind) return null;
  const normalized = String(kind).toLowerCase();
  if (normalized === 'positive') return 'Promoter question';
  if (normalized === 'negative') return 'Detractor question';
  return kind;
};

const npsCategory = (score) => {
  if (score == null || Number.isNaN(Number(score))) {
    return { label: 'No score', tone: 'default' };
  }
  const n = Number(score);
  if (n <= 6) return { label: 'Detractor', tone: 'detractor' };
  if (n <= 8) return { label: 'Neutral', tone: 'passive' };
  return { label: 'Promoter', tone: 'promoter' };
};

const npsColors = {
  detractor: { main: '#c62828', bg: '#ffebee', border: '#ef9a9a' },
  passive: { main: '#ed6c02', bg: '#fff8e1', border: '#ffcc02' },
  promoter: { main: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
  default: { main: '#546e7a', bg: '#eceff1', border: '#b0bec5' },
};

const formatSubmitted = (iso) => {
  if (!iso) return '—';
  try {
    return format(new Date(iso), "MMM d, yyyy 'at' h:mm a");
  } catch {
    return iso;
  }
};

const TextBlock = ({ title, children }) => {
  if (!children || !String(children).trim()) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
        {children}
      </Typography>
    </Box>
  );
};

const npsAssignSelectValue = (item) => {
  const team = item?.assigned_to;
  return team && NPS_ASSIGN_TEAMS.includes(team) ? team : '';
};

const NpsResultCard = ({
  item,
  actionBusy,
  onRequestMarkComplete,
  onAssignTeam,
  onOpenDetailsDialog,
  onRequestReopen,
  onOpenCapDrawer,
}) => {
  const [assignMenuAnchor, setAssignMenuAnchor] = useState(null);
  const assignMenuOpen = Boolean(assignMenuAnchor);

  const category = npsCategory(item.nps_score);
  const colors = npsColors[category.tone];
  const surveyLabel = getNpsSurveyLabel(item.survey_type);
  const name = [item.fname, item.lname].filter(Boolean).join(' ').trim() || 'Unknown respondent';
  const statusChip = npsStatusChipProps(item);
  const resolutionDisplay = showNpsResolutionUi(item) ? getNpsResolutionDisplay(item) : { mode: 'none' };
  const assignedTeam = npsAssignSelectValue(item) || item.assigned_to;

  const handleAssignPick = (team) => {
    setAssignMenuAnchor(null);
    onAssignTeam(item, team);
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2.5,
        borderRadius: 2,
        borderLeft: `4px solid ${colors.main}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: colors.bg,
              border: `1px solid ${colors.border}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="h5" fontWeight={700} lineHeight={1} sx={{ color: colors.main }}>
              {item.nps_score ?? '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.main, fontSize: '0.65rem', fontWeight: 600 }}>
              NPS
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {name}
            </Typography>
            {item.email && (
              <NpsMemberEmailLookup email={item.email} onOpenCapDrawer={onOpenCapDrawer} />
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Submitted {formatSubmitted(item.submitted_at)}
            </Typography>
          </Box>
        </Box>
        <Stack spacing={1} alignItems="flex-end" sx={{ flexShrink: 0 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ justifyContent: 'flex-end' }}>
            <Chip size="small" label={surveyLabel} variant="outlined" />
            <Chip size="small" label={category.label} sx={{ bgcolor: colors.bg, color: colors.main, borderColor: colors.border }} />
            <Chip size="small" label={statusChip.label} color={statusChip.color} variant={statusChip.variant} />
          </Stack>
          {assignedTeam && (
            <Box
              sx={{
                px: 2.5,
                py: 0.75,
                borderRadius: 1,
                bgcolor: '#e3f2fd',
                border: '1px solid #90caf9',
                minWidth: 148,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                component="div"
                fontWeight={700}
                sx={{ color: '#1565c0', letterSpacing: 0.8, fontSize: '0.7rem' }}
              >
                {String(assignedTeam).toUpperCase()}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>

      <Divider sx={{ my: 0.5 }} />

      <TextBlock title="Why this score?" children={item.reason_text} />
      <TextBlock title="What could we improve?" children={item.improve_text} />

      {item.follow_up_text && (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: '#f5f7fa',
            border: '1px solid #cfd8dc',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: '#546e7a' }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Survey response
            </Typography>
            {followUpKindLabel(item.follow_up_kind) && (
              <Chip
                size="small"
                label={followUpKindLabel(item.follow_up_kind)}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', borderColor: '#b0bec5' }}
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
            {item.follow_up_text}
          </Typography>
        </Box>
      )}

      {Array.isArray(item.purchase_drivers) && item.purchase_drivers.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Purchase drivers
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
            {item.purchase_drivers.map((driver) => (
              <Chip key={driver} label={driver} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}

      {(resolutionDisplay.mode === 'marked_complete' || resolutionDisplay.mode === 'closed') && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <TaskAltIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {resolutionDisplay.mode === 'closed' ? 'Closed' : 'Marked complete'}
            {(resolutionDisplay.by || resolutionDisplay.at) && (
              <>
                {' · '}
                {[resolutionDisplay.by, resolutionDisplay.at ? formatSubmitted(resolutionDisplay.at) : null]
                  .filter(Boolean)
                  .join(' · ')}
              </>
            )}
          </Typography>
        </Box>
      )}

      {resolutionDisplay.mode === 'note' && (
        <Box
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: 1.5,
            bgcolor: '#e8f5e9',
            border: '2px solid #2e7d32',
            boxShadow: 'inset 4px 0 0 #1b5e20',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <TaskAltIcon sx={{ fontSize: 18, color: '#1b5e20' }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1b5e20' }}>
              Team resolution
            </Typography>
          </Box>
          {(resolutionDisplay.by || resolutionDisplay.at) && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {[resolutionDisplay.by, resolutionDisplay.at ? formatSubmitted(resolutionDisplay.at) : null]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
            {resolutionDisplay.body}
          </Typography>
        </Box>
      )}

      {canNpsQueueActions(item) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            disabled={actionBusy}
            onClick={() => onRequestMarkComplete(item)}
          >
            Mark complete
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={actionBusy}
            onClick={() => onOpenDetailsDialog(item)}
          >
            Resolve with details
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={actionBusy}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(e) => setAssignMenuAnchor(e.currentTarget)}
            aria-haspopup="listbox"
            aria-expanded={assignMenuOpen}
          >
            Assign
          </Button>
          <Menu
            anchorEl={assignMenuAnchor}
            open={assignMenuOpen}
            onClose={() => setAssignMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {getNpsItemStatus(item) === 'assigned' && (
              <MenuItem onClick={() => handleAssignPick('')}>
                Unassign
              </MenuItem>
            )}
            {NPS_ASSIGN_TEAMS.map((team) => (
              <MenuItem
                key={team}
                selected={npsAssignSelectValue(item) === team}
                onClick={() => handleAssignPick(team)}
              >
                {team}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}

      {canNpsLeadershipClose(item) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            disabled={actionBusy}
            onClick={() => onRequestMarkComplete(item)}
          >
            Mark complete
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            disabled={actionBusy}
            onClick={() => onRequestReopen(item)}
          >
            Reopen
          </Button>
        </Box>
      )}

      <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
        ID {item.id} · Form {item.form_id}
      </Typography>
    </Paper>
  );
};

const NpsPage = () => {
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const { allowed, checked } = useNpsAccess();

  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [statuses, setStatuses] = useState(DEFAULT_NPS_STATUS_FILTER);
  const [surveyTypes, setSurveyTypes] = useState([]);
  const [npsBucket, setNpsBucket] = useState('');
  const [submittedFrom, setSubmittedFrom] = useState('');
  const [submittedTo, setSubmittedTo] = useState('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [matchingTotal, setMatchingTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [completeConfirmItem, setCompleteConfirmItem] = useState(null);
  const [reopenConfirmItem, setReopenConfirmItem] = useState(null);
  const [detailsItem, setDetailsItem] = useState(null);
  const [detailsText, setDetailsText] = useState('');
  const [capDrawerRecord, setCapDrawerRecord] = useState(null);

  const loadMoreRef = useRef(null);
  const fetchingRef = useRef(false);
  const pageRef = useRef(1);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const hasListFilters =
    surveyTypes.length > 0 ||
    Boolean(npsBucket) ||
    Boolean(submittedFrom) ||
    Boolean(submittedTo) ||
    Boolean(search);

  const listTotal = matchingTotal;
  const hasMore = results.length < listTotal;

  const applySearch = () => {
    setSearch(searchDraft.trim());
  };

  const clearListFilters = () => {
    setSurveyTypes([]);
    setNpsBucket('');
    setSubmittedFrom('');
    setSubmittedTo('');
    setSearch('');
    setSearchDraft('');
    setStatuses(DEFAULT_NPS_STATUS_FILTER);
  };

  const statusFilterNarrowsList =
    statuses.length === 0 || !isDefaultNpsStatusFilter(statuses);

  const applyItemAfterStatusChange = useCallback(
    (previousItem, updated) => {
      const prevStatus = getNpsItemStatus(previousItem);
      const nextStatus = getNpsItemStatus(updated);

      setOpenCount((prev) => {
        let next = prev;
        if (prevStatus === 'open') next = Math.max(0, next - 1);
        if (nextStatus === 'open') next += 1;
        broadcastNpsOpenCount(next);
        return next;
      });

      const wasInFilter = itemMatchesNpsStatusFilter(previousItem, statuses);
      const nowInFilter = itemMatchesNpsStatusFilter(updated, statuses);

      if (wasInFilter && nowInFilter) {
        setResults((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      } else if (wasInFilter && !nowInFilter) {
        setResults((prev) => prev.filter((row) => row.id !== updated.id));
        setMatchingTotal((prev) => Math.max(0, prev - 1));
      } else if (!wasInFilter && nowInFilter) {
        setResults((prev) => {
          const exists = prev.some((row) => row.id === updated.id);
          if (exists) {
            return prev.map((row) => (row.id === updated.id ? updated : row));
          }
          return [updated, ...prev];
        });
        setMatchingTotal((prev) => prev + 1);
      }
    },
    [statuses]
  );

  const handleConfirmMarkComplete = async () => {
    if (!completeConfirmItem) return;
    const id = completeConfirmItem.id;
    setActionBusyId(id);
    setActionError(null);
    try {
      const updated = await updateNpsResult(id, { status: 'closed' });
      applyItemAfterStatusChange(completeConfirmItem, updated);
      setCompleteConfirmItem(null);
    } catch (err) {
      setActionError(err.message || 'Failed to mark complete');
    } finally {
      setActionBusyId(null);
    }
  };

  const handleConfirmReopen = async () => {
    if (!reopenConfirmItem) return;
    const id = reopenConfirmItem.id;
    setActionBusyId(id);
    setActionError(null);
    try {
      const updated = await updateNpsResult(id, { status: 'open' });
      applyItemAfterStatusChange(reopenConfirmItem, updated);
      setReopenConfirmItem(null);
    } catch (err) {
      setActionError(err.message || 'Failed to reopen');
    } finally {
      setActionBusyId(null);
    }
  };

  const handleAssignTeam = async (item, team) => {
    if (!item) return;
    const current = npsAssignSelectValue(item);
    if (team === current) return;
    if (!team && getNpsItemStatus(item) !== 'assigned') return;

    const id = item.id;
    setActionBusyId(id);
    setActionError(null);
    try {
      const updated = await updateNpsResult(id, { assignedTo: team });
      applyItemAfterStatusChange(item, updated);
    } catch (err) {
      setActionError(err.message || 'Failed to assign');
    } finally {
      setActionBusyId(null);
    }
  };

  const handleOpenDetailsDialog = (item) => {
    setActionError(null);
    setDetailsItem(item);
    setDetailsText('');
  };

  const handleCloseDetailsDialog = () => {
    if (actionBusyId) return;
    setDetailsItem(null);
    setDetailsText('');
  };

  const handleSubmitDetailsResolve = async () => {
    if (!detailsItem) return;
    const resolution = detailsText.trim();
    if (!resolution) return;

    const id = detailsItem.id;
    setActionBusyId(id);
    setActionError(null);
    try {
      const updated = await updateNpsResult(id, { status: 'resolved', resolution });
      applyItemAfterStatusChange(detailsItem, updated);
      setDetailsItem(null);
      setDetailsText('');
    } catch (err) {
      setActionError(err.message || 'Failed to save resolution');
    } finally {
      setActionBusyId(null);
    }
  };

  const syncOpenCountFromApi = useCallback(async () => {
    const open = await fetchNpsOpenCount();
    if (open !== null) {
      setOpenCount(open);
      broadcastNpsOpenCount(open);
    }
  }, []);

  const applyListMetaFromResponse = useCallback((data, pageNum) => {
    setTotal(data.total ?? 0);
    setMatchingTotal(
      typeof data.matchingTotal === 'number' ? data.matchingTotal : (data.total ?? 0)
    );
    setPage(data.page ?? pageNum);
    syncOpenCountFromApi();
  }, [syncOpenCountFromApi]);

  const requestNpsPage = useCallback(
    async (pageNum) => {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error('Could not get access token');

      const params = buildNpsResultsSearchParams({
        page: pageNum,
        statuses,
        surveyTypes,
        npsBucket,
        submittedFrom,
        submittedTo,
        search,
      });

      const response = await fetch(`${NPS_RESULTS_URL}?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to load NPS results');
      }

      return response.json();
    },
    [statuses, surveyTypes, npsBucket, submittedFrom, submittedTo, search]
  );

  const refreshLoadedPages = useCallback(
    async ({ silent = false } = {}) => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const lastPage = pageRef.current;

      try {
        let combined = [];
        let lastPayload = null;

        for (let p = 1; p <= lastPage; p += 1) {
          const data = await requestNpsPage(p);
          lastPayload = data;
          combined = combined.concat(data.results || []);
        }

        if (lastPayload) {
          applyListMetaFromResponse(lastPayload, lastPage);
        }
        setResults(combined);
      } catch (err) {
        if (!silent) {
          setError(err.message || 'Failed to load NPS results');
          setResults([]);
          setMatchingTotal(0);
        }
      } finally {
        fetchingRef.current = false;
        if (!silent) setLoading(false);
      }
    },
    [requestNpsPage, applyListMetaFromResponse]
  );

  const fetchPage = useCallback(
    async (pageNum, { append } = {}) => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setError(null);
      }

      try {
        const data = await requestNpsPage(pageNum);
        const batch = data.results || [];

        applyListMetaFromResponse(data, pageNum);
        setResults((prev) => (append ? [...prev, ...batch] : batch));
      } catch (err) {
        setError(err.message || 'Failed to load NPS results');
        if (!append) {
          setResults([]);
          setMatchingTotal(0);
        }
      } finally {
        fetchingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [requestNpsPage, applyListMetaFromResponse]
  );

  useEffect(() => {
    if (accounts.length === 0) {
      navigate('/');
    }
  }, [accounts, navigate]);

  useEffect(() => {
    if (!checked || !allowed) return;
    pageRef.current = 1;
    setResults([]);
    setPage(1);
    fetchPage(1, { append: false });
  }, [checked, allowed, fetchPage]);

  useEffect(() => {
    if (!checked || !allowed) return undefined;

    const intervalId = setInterval(() => {
      refreshLoadedPages({ silent: true });
    }, NPS_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [checked, allowed, refreshLoadedPages]);

  useEffect(() => {
    if (!allowed || loading || loadingMore || !hasMore) return;

    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !fetchingRef.current) {
          fetchPage(page + 1, { append: true });
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [allowed, loading, loadingMore, hasMore, page, fetchPage]);

  if (!checked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return <NavigateToHub />;
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', pb: 6 }}>
      <CapContactsDrawer
        open={Boolean(capDrawerRecord)}
        record={capDrawerRecord}
        onClose={() => setCapDrawerRecord(null)}
      />
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <RateReviewIcon />
              <Typography variant="h5" component="h1" fontWeight={600}>
                NPS Feedback
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Assign and resolve in the field; leadership marks resolved cases complete when satisfied
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Paper
              elevation={0}
              sx={{ px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 1.5, textAlign: 'center', minWidth: 88 }}
            >
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {openCount}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Open</Typography>
            </Paper>
            <Paper
              elevation={0}
              sx={{ px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 1.5, textAlign: 'center', minWidth: 88 }}
            >
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {total}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Total</Typography>
            </Paper>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          {!loading && (
            <Typography variant="body2" color="text.secondary">
              Showing {results.length} of {listTotal} matching
              {statuses.length === 0 && !hasListFilters ? ' (all responses)' : ''}
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              size="small"
              label="Search email or last name"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applySearch();
              }}
              sx={{ minWidth: 220, flex: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={applySearch} aria-label="Apply search">
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="nps-status-filter-label">Status</InputLabel>
              <Select
                labelId="nps-status-filter-label"
                multiple
                value={statuses}
                onChange={(e) => {
                  const value = e.target.value;
                  setStatuses(typeof value === 'string' ? value.split(',') : value);
                }}
                input={<OutlinedInput label="Status" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <Typography variant="body2" component="span" color="text.secondary">
                        All statuses
                      </Typography>
                    );
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((v) => {
                        const opt = NPS_STATUS_OPTIONS.find((o) => o.value === v);
                        return <Chip key={v} size="small" label={opt?.label ?? v} />;
                      })}
                    </Box>
                  );
                }}
              >
                {NPS_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox checked={statuses.includes(opt.value)} size="small" />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="nps-survey-filter-label">Survey type</InputLabel>
              <Select
                labelId="nps-survey-filter-label"
                multiple
                value={surveyTypes}
                onChange={(e) => {
                  const value = e.target.value;
                  setSurveyTypes(typeof value === 'string' ? value.split(',') : value);
                }}
                input={<OutlinedInput label="Survey type" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <Typography variant="body2" component="span" color="text.secondary">All survey types</Typography>;
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((v) => (
                        <Chip key={v} size="small" label={getNpsSurveyLabel(v)} />
                      ))}
                    </Box>
                  );
                }}
              >
                {NPS_SURVEY_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox checked={surveyTypes.includes(opt.value)} size="small" />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 168 }}>
              <InputLabel id="nps-bucket-filter-label">Score</InputLabel>
              <Select
                labelId="nps-bucket-filter-label"
                value={npsBucket === 'passive' ? 'neutral' : npsBucket}
                label="Score"
                onChange={(e) => setNpsBucket(e.target.value)}
              >
                {NPS_BUCKET_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(hasListFilters || searchDraft || statusFilterNarrowsList) && (
              <Button size="small" onClick={clearListFilters}>
                Clear filters
              </Button>
            )}
          </Box>

          <NpsSubmittedDateRangeSlider
            submittedFrom={submittedFrom}
            submittedTo={submittedTo}
            onChange={(from, to) => {
              setSubmittedFrom(from);
              setSubmittedTo(to);
            }}
          />

          {search && (
            <Typography variant="caption" color="text.secondary">
              Search: &ldquo;{search}&rdquo;
            </Typography>
          )}
        </Stack>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {!loading && !error && results.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {hasListFilters || statusFilterNarrowsList
              ? 'No responses match your current filters.'
              : 'No NPS responses found.'}
          </Typography>
        </Paper>
      )}

      {!loading && results.length > 0 && (
        <Stack spacing={2}>
          {results.map((item) => (
            <NpsResultCard
              key={item.id}
              item={item}
              actionBusy={actionBusyId === item.id}
              onRequestMarkComplete={setCompleteConfirmItem}
              onAssignTeam={handleAssignTeam}
              onOpenDetailsDialog={handleOpenDetailsDialog}
              onRequestReopen={setReopenConfirmItem}
              onOpenCapDrawer={setCapDrawerRecord}
            />
          ))}
        </Stack>
      )}

      {loadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && hasMore && <Box ref={loadMoreRef} sx={{ height: 24 }} aria-hidden />}

      <Dialog
        open={Boolean(completeConfirmItem)}
        onClose={() => !actionBusyId && setCompleteConfirmItem(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mark complete?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {completeConfirmItem && getNpsItemStatus(completeConfirmItem) === 'resolved'
              ? 'Confirm the team resolution is correct and close this case. No further action will be required.'
              : 'Close this response without a team resolution — for feedback that needs no outreach or follow-up.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteConfirmItem(null)} disabled={Boolean(actionBusyId)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmMarkComplete}
            disabled={Boolean(actionBusyId)}
          >
            {actionBusyId ? 'Saving…' : 'Mark complete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(reopenConfirmItem)}
        onClose={() => !actionBusyId && setReopenConfirmItem(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reopen this response?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark the NPS response as open again. Resolution notes stay in history but won&apos;t be shown while it&apos;s open.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenConfirmItem(null)} disabled={Boolean(actionBusyId)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmReopen}
            disabled={Boolean(actionBusyId)}
          >
            {actionBusyId ? 'Saving…' : 'Reopen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detailsItem)}
        onClose={handleCloseDetailsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resolve with details</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Describe what was done to resolve the customer's feedback.
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            minRows={4}
            fullWidth
            label="Resolution notes"
            placeholder="What we did: called member, explained billing, …"
            value={detailsText}
            onChange={(e) => setDetailsText(e.target.value)}
            disabled={Boolean(actionBusyId)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog} disabled={Boolean(actionBusyId)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitDetailsResolve}
            disabled={Boolean(actionBusyId) || !detailsText.trim()}
          >
            {actionBusyId ? 'Saving…' : 'Save & resolve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function NavigateToHub() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/global-search', { replace: true });
  }, [navigate]);
  return null;
}

export default NpsPage;
